import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import KeyOutlined from '@ant-design/icons/KeyOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const authUser = localStorage.getItem('auth_user');
  const user = authUser ? JSON.parse(authUser) : null;

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [namaKaryawan, setNamaKaryawan] = useState('');
  const [namaGudang, setNamaGudang] = useState('');

  useEffect(() => {
    if (user && user.nm_karyawan) {
      setNamaKaryawan(user.nm_karyawan);
    } else if (user) {
      const token = (user && user.token) || localStorage.getItem('token') || '';
      fetch(`${import.meta.env.VITE_APP_API_URL}/karyawan`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const matched = data.find((k) => k.id == user.id_karyawan || k.email == user.username || k.nm_karyawan == user.username);
            if (matched) setNamaKaryawan(matched.nm_karyawan);
          }
        })
        .catch(() => {});
    }

    if (user && user.nm_gudang) {
      setNamaGudang(user.nm_gudang);
    } else if (user && user.id_gudang) {
      const token = (user && user.token) || localStorage.getItem('token') || '';
      fetch(`${import.meta.env.VITE_APP_API_URL}/gudang`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const matched = data.find((g) => g.id == user.id_gudang);
            if (matched) setNamaGudang(matched.nm_gudang);
          }
        })
        .catch(() => {});
    }
  }, []);

  // Change Password Modal States
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const navigate = useNavigate();
  const handleLogout = async () => {
    localStorage.removeItem('auth_user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('auth_user');
    sessionStorage.removeItem('token');
    navigate('/login', { replace: true });
  };

  // Open & Close Change Password Modal
  const handleOpenPasswordModal = () => {
    setOpen(false);
    setPasswordData({ password: '', confirmPassword: '' });
    setChangeError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setOpenChangePassword(true);
  };

  const handleClosePasswordModal = () => {
    if (changeLoading) return;
    setOpenChangePassword(false);
  };

  // Handle Form Change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setChangeError('');
  };

  // Submit Change Password
  const handleSubmitChangePassword = async () => {
    if (!passwordData.password) {
      setChangeError('Password baru wajib diisi.');
      return;
    }
    if (passwordData.password.length < 6) {
      setChangeError('Password minimal harus 6 karakter.');
      return;
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      setChangeError('Konfirmasi password tidak cocok.');
      return;
    }

    setChangeLoading(true);
    setChangeError('');

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (user && user.token) {
        headers['Authorization'] = `Bearer ${user.token}`;
      }

      const userId = user ? (user.id || user.user_id || user.id_user) : '';
      const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/user/${userId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          password: passwordData.password
        })
      });

      if (response.ok) {
        setOpenChangePassword(false);
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: 'Password Anda telah berhasil diperbarui!'
        });
      } else {
        const errData = await response.json();
        setChangeError(errData.message || 'Gagal mengubah password.');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setChangeError('Terjadi kesalahan koneksi saat mengubah password.');
    } finally {
      setChangeLoading(false);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="User Profile" disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar alt="profile user" src={avatar1} size="sm" sx={{ '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } }} />
        </ButtonBase>
      </Tooltip>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 260, minWidth: 220, maxWidth: { xs: 240, md: 260 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                        <Avatar alt="profile user" src={avatar1} sx={{ width: 40, height: 40 }} />
                        <Stack>
                          <Typography variant="h6">{namaKaryawan || user?.nama || user?.username || 'Admin'}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {user?.level == 1 ? 'Administrator' : user?.level == 2 ? 'Operator Gudang' : 'User'}
                            {namaGudang ? ` (${namaGudang})` : ''}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<KeyOutlined />}
                        onClick={handleOpenPasswordModal}
                        size="small"
                      >
                        Ubah Password
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        startIcon={<LogoutOutlined />}
                        onClick={handleLogout}
                        size="small"
                      >
                        Keluar / Logout
                      </Button>
                    </Stack>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* Modal Dialog Change Password */}
      <Dialog open={openChangePassword} onClose={handleClosePasswordModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyOutlined style={{ color: '#1890ff' }} />
          <Typography variant="h5">Ubah Password</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {changeError && <Alert severity="error">{changeError}</Alert>}

            <TextField
              fullWidth
              size="small"
              label="Password Baru"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={passwordData.password}
              onChange={handlePasswordChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Konfirmasi Password Baru"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                      {showConfirmPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePasswordModal} color="secondary" disabled={changeLoading}>
            Batal
          </Button>
          <Button
            onClick={handleSubmitChangePassword}
            variant="contained"
            color="primary"
            disabled={changeLoading}
            startIcon={changeLoading ? <CircularProgress size={16} color="inherit" /> : <KeyOutlined />}
          >
            {changeLoading ? 'Menyimpan...' : 'Simpan Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
